// SIL-1313/SIL-1322: Exchange buyer verification (KYB). Spec:
// docs/superpowers/specs/2026-09-28-exchange-buyer-verification-and-gating-design.md
import { z } from 'zod';

/** Exchange User Agreement version accepted at submit. Bump when SIL-1316 lands. */
export const EXCHANGE_UA_VERSION = '2026-09-28-draft';

/**
 * SIL-1359: the sentinel `buyer_verification.exchange_ua_version` value
 * db/schema/952_buyer_verification_demo_orgs.sql stamps on demo business
 * orgs. Demo orgs are a deliberate, permanent exemption from the
 * re-acceptance gate below — they exist to keep demos showing exact prices,
 * not to represent a real legal acceptance, so a future EXCHANGE_UA_VERSION
 * bump must never gate them. See 963's migration docstring for the
 * counterpart on the DB side.
 */
export const EXCHANGE_UA_DEMO_SEED_VERSION = 'demo-seed';

/**
 * SIL-1359: true iff `version` (a `buyer_verification.exchange_ua_version`
 * value) satisfies the current Exchange User Agreement — either it matches
 * the live `EXCHANGE_UA_VERSION` exactly, or it is the demo-seed sentinel
 * (see `EXCHANGE_UA_DEMO_SEED_VERSION` above). Shared by
 * `middleware/clientAuth.ts` and `mcp/auth.ts` (both build the identical SQL
 * predicate against this pair of constants — see those files for why the
 * predicate itself is duplicated rather than this constant) and by
 * `routes/client/buyer-verification.ts`'s `needsAgreementReacceptance` flag.
 */
export function isExchangeUaCurrent(version: string | null | undefined): boolean {
  return version === EXCHANGE_UA_VERSION || version === EXCHANGE_UA_DEMO_SEED_VERSION;
}

export const ENTITY_TYPES = ['ai_lab', 'neocloud', 'enterprise', 'research', 'reseller'] as const;
export const PURCHASE_VOLUMES = ['under_1m', '1m_10m', '10m_50m', '50m_100m', 'over_100m'] as const;
export const BUYER_VERIFICATION_DOC_TYPES = ['application/pdf', 'image/png', 'image/jpeg'] as const;
export const BUYER_VERIFICATION_MAX_DOC_BYTES = 10 * 1024 * 1024;

const e164 = z.string().regex(/^\+[1-9][0-9]{7,14}$/, 'Use international format, for example +15555550142');
const text = (max: number) => z.string().trim().max(max);

export const buyerVerificationDraftSchema = z
  .object({
    legalName: text(300),
    entityType: z.enum(ENTITY_TYPES),
    country: z.string().regex(/^[A-Z]{2}$/),
    registrationNumber: text(100),
    website: text(300),
    registeredAddress: text(1000),
    signatoryName: text(200),
    signatoryTitle: text(200),
    beneficialOwners: text(2000),
    purchaseVolume: z.enum(PURCHASE_VOLUMES),
    primaryUse: text(500),
    authorityConfirmed: z.boolean(),
  })
  .partial()
  .strict();
export type BuyerVerificationDraft = z.infer<typeof buyerVerificationDraftSchema>;

/**
 * SIL-1575: stable codes `POST /sms/send` returns for a Twilio Verify
 * failure, classified by `twilioCode` rather than echoed as 502 across the
 * board (`apps/api/src/services/buyer-verification/twilio-verify.ts`'s
 * `classifyTwilioVerifyError`). `PHONE_NOT_SUPPORTED` is the pre-send
 * allow-list rejection (SIL-1506, unrelated to Twilio's own response) - these
 * three are classifications of an actual Twilio Verify failure. Shared so
 * the client's error-copy table (`apps/customer/src/components/exchange/
 * errors.ts`) is typed to cover every code the server can send - a code
 * added here that copy forgets to translate is a compile error there, not a
 * silent generic toast.
 */
export const SMS_SEND_TWILIO_ERROR_CODES = ['PHONE_UNDELIVERABLE', 'SMS_PROVIDER_UNAVAILABLE', 'RATE_LIMITED'] as const;
export type SmsSendTwilioErrorCode = (typeof SMS_SEND_TWILIO_ERROR_CODES)[number];

export const smsSendSchema = z.object({ phone: e164 }).strict();
export const smsVerifySchema = z.object({ code: z.string().regex(/^[0-9]{6}$/) }).strict();
export const documentUploadUrlSchema = z
  .object({
    contentType: z.enum(BUYER_VERIFICATION_DOC_TYPES),
    sizeBytes: z.number().int().positive().max(BUYER_VERIFICATION_MAX_DOC_BYTES),
    fileName: text(200).optional(),
  })
  .strict();
// The client never sends an S3 key; the server derives staging and final keys (review #3).
export const documentConfirmSchema = z.object({ uploadId: z.string().uuid() }).strict();
export const submitSchema = z.object({ exchangeUaVersion: z.literal(EXCHANGE_UA_VERSION) }).strict();
/**
 * SIL-1359: the lightweight re-acceptance body — same single-field shape as
 * `submitSchema` (the client always sends the CURRENT version, checked by
 * the `z.literal`), reused rather than duplicated since the two requests are
 * shaped identically. `routes/client/buyer-verification.ts`'s
 * `POST /agreement/accept` validates against this alias.
 */
export const reacceptExchangeUaSchema = submitSchema;
/** Reject and revoke: `reason` is shown to the client; `internalNote` is ops-only. */
export const opsRejectSchema = z
  .object({ reason: z.string().trim().min(3).max(2000), internalNote: z.string().trim().max(2000).optional() })
  .strict();
/** Approve: when the latest screen has hits, the reviewer must acknowledge them. */
export const opsApproveSchema = z.object({ acknowledgeHits: z.boolean().optional() }).strict();

export type BuyerVerificationStatus = 'unverified' | 'draft' | 'pending' | 'verified' | 'rejected';

export interface BuyerVerificationResponse {
  status: BuyerVerificationStatus;
  /** null for org members: draft PII is for org admins only (security review #15). */
  draft: null | (BuyerVerificationDraft & {
    documentUploaded: boolean;
    phoneMasked: string | null;
    phoneVerified: boolean;
  });
  accountCreatedAt: string;
  email: string;
  exchangeUaVersion: string;
  rejectionReason: string | null;
  submittedAt: string | null;
  canEdit: boolean;
  /**
   * SIL-1359: true iff this org is `verified` but its accepted
   * `exchange_ua_version` is stale (and not the demo-seed exemption) —
   * i.e. `buyerVerified` on the server session is currently false for this
   * reason alone, not because the org was never verified. The buyer UI
   * routes this case to the agreement-only re-acceptance step
   * (`POST /agreement/accept`) rather than step 1 of the full KYB wizard.
   */
  needsAgreementReacceptance: boolean;
}

export interface SanctionsHit {
  query: string;
  name: string;
  source: string;
  score: number | null;
}
export type SanctionsResult =
  | { queries: string[]; hits: SanctionsHit[]; sourcesUsed: string[]; checkedAt: string }
  | { error: string; checkedAt: string };

const sanctionsHitSchema = z.object({
  query: z.string(),
  name: z.string(),
  source: z.string(),
  score: z.number().nullable(),
});
/** Runtime shape of `buyer_verification_screen.result` (jsonb): a clean/hit result or the error variant. */
export const sanctionsResultSchema = z.union([
  z.object({
    queries: z.array(z.string()),
    hits: z.array(sanctionsHitSchema),
    sourcesUsed: z.array(z.string()),
    checkedAt: z.string(),
  }),
  z.object({ error: z.string(), checkedAt: z.string() }),
]);

/** SIL-2422: max screens returned in the ops detail history. */
export const OPS_SCREEN_HISTORY_LIMIT = 50;

/** One `buyer_verification_screen` row for the ops screen history. `error` is set for a failed or unreadable screen. */
export const opsSanctionsScreenSchema = z.object({
  id: z.string().uuid(),
  screenedAt: z.string(),
  source: z.enum(['submit', 'ops']),
  screenedBy: z.string().nullable(),
  hitCount: z.number().int().nullable(),
  queries: z.array(z.string()),
  hits: z.array(sanctionsHitSchema),
  error: z.string().nullable(),
});
export type OpsSanctionsScreen = z.infer<typeof opsSanctionsScreenSchema>;

export interface OpsBuyerVerificationListItem {
  customerOrgId: string;
  legalName: string | null;
  entityType: string | null;
  country: string | null;
  status: Exclude<BuyerVerificationStatus, 'unverified'>;
  submittedAt: string | null;
  sanctionsHitCount: number | null;
  sanctionsError: boolean;
}

export interface OpsBuyerVerificationDetail extends OpsBuyerVerificationListItem {
  fields: BuyerVerificationDraft & { phoneE164: string | null; phoneVerifiedAt: string | null };
  documentUrl: string | null;
  /** Latest screen; `current` is true when screened_at >= submitted_at. */
  sanctions: (SanctionsResult & { source: 'submit' | 'ops'; screenedBy: string | null; current: boolean }) | null;
  /** SIL-2422: id of the screen that gates Approve (same row `sanctions` describes); null when none. */
  latestScreenId: string | null;
  /** SIL-2422: every screen, newest first, at most OPS_SCREEN_HISTORY_LIMIT. The first entry is the latest. */
  screens: OpsSanctionsScreen[];
  rejectionReason: string | null;
  exchangeUa: { version: string | null; acceptedAt: string | null; ip: string | null };
  events: {
    fromStatus: string | null;
    toStatus: string;
    actorKind: string;
    actorId: string | null;
    note: string | null;
    createdAt: string;
  }[];
  reviewNote: string | null;
}
